import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

export type UserMultiSelectOption = { userId: number; label: string };

export type UserMultiSelectProps = {
  options: UserMultiSelectOption[];
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  lockedIds?: Set<number>;
  placeholder?: string;
  fullWidth?: boolean;
  compact?: boolean;
  sx?: SxProps<Theme>;
};

/** Multi-user Autocomplete styled like DatePicker compact field. */
export function UserMultiSelect({
  options,
  value,
  onChange,
  disabled = false,
  lockedIds,
  placeholder = 'Select',
  fullWidth = true,
  compact = true,
  sx,
}: UserMultiSelectProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const optionById = useMemo(() => {
    const map = new Map<number, UserMultiSelectOption>();
    for (const option of options) map.set(option.userId, option);
    return map;
  }, [options]);

  // Never render bare userId as chip text; parent must supply labels via options.
  const selected = value.map(
    (id) => optionById.get(id) || { userId: id, label: '-' },
  );
  const empty = selected.length === 0;

  // Align with DatePicker compact styles; stay within parent width; chips wrap with full label.
  const mergedSx = {
    position: 'relative',
    maxWidth: '100%',
    ...(compact ? { lineHeight: 1.2 } : {}),
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall, & .MuiFilledInput-root.MuiInputBase-sizeSmall, & .MuiInputBase-root.MuiInputBase-sizeSmall': {
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      maxWidth: '100%',
      boxSizing: 'border-box',
      paddingTop: '3px',
      paddingBottom: '3px',
      paddingLeft: '3px',
      paddingRight: '32px',
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    '& .MuiAutocomplete-input': {
      // Empty: room for full placeholder "Select"; with tags: shrink so chips own the row.
      minWidth: empty ? '4.5em !important' : '0 !important',
      width: empty ? 'auto' : 0,
      flexGrow: empty ? 1 : 0,
    },
    '& .MuiInputBase-input': {
      fontSize: (theme.typography as { body2?: { fontSize?: string | number } }).body2?.fontSize || undefined,
      lineHeight: 1.2,
      paddingTop: 4,
      paddingBottom: 4,
    },
    '& .MuiInputAdornment-positionEnd': {
      position: 'absolute',
      right: 2,
      top: '50%',
      transform: 'translateY(-50%)',
      margin: 0,
      height: 'auto',
      maxHeight: '100%',
      pointerEvents: 'auto',
    },
    '& .MuiInputAdornment-positionEnd .MuiIconButton-root:hover svg': {
      color: theme.palette.primary.main,
    },
    '& .MuiChip-root': {
      height: 'auto',
      maxWidth: '100%',
      fontSize: 12,
      '& .MuiChip-label': {
        px: 0.75,
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        display: 'block',
      },
    },
  } as SxProps<Theme>;

  return (
    <Autocomplete
      multiple
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(left, right) => left.userId === right.userId}
      disableClearable
      forcePopupIcon={false}
      onChange={(_event, next) => {
        const ids = next.map((option) => option.userId);
        if (lockedIds) {
          for (const id of lockedIds) {
            if (!ids.includes(id)) ids.push(id);
          }
        }
        onChange(ids);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const locked = lockedIds?.has(option.userId);
          return (
            <Chip
              key={key}
              label={option.label}
              size="small"
              title={option.label}
              {...tagProps}
              onDelete={locked || disabled ? undefined : tagProps.onDelete}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={empty ? placeholder : undefined}
          fullWidth={fullWidth}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  disabled={disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (!disabled) setOpen(true);
                  }}
                  sx={{ p: 0.5 }}
                  aria-label="open user select"
                >
                  <PersonOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={mergedSx}
        />
      )}
      sx={[
        { width: '100%', maxWidth: '100%', minWidth: 0 },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
